/**
 * Start an audio element and resolve only after its playback position advances.
 * HTMLMediaElement.play() can resolve before audio data is decoded or audible.
 *
 * @param {HTMLAudioElement} player
 * @param {number} [timeoutMs]
 * @returns {Promise<void>}
 */
export function playAndVerifyAudio(player, timeoutMs = 3000) {
  let expectedSrc = player.src;
  // play() restarts ended media at zero. Measure progress from that new
  // beginning, not from the previous playback's final position.
  let initialTime = player.ended ? 0 : player.currentTime;

  return new Promise((resolve, reject) => {
    let timeout = setTimeout(() => {
      if (player.src != expectedSrc) {
        sourceChanged();
      } else {
        fail(new Error("Text-to-speech audio did not begin playing"));
      }
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timeout);
      player.removeEventListener("timeupdate", checkProgress);
      player.removeEventListener("ended", checkEnded);
      player.removeEventListener("error", mediaError);
    }

    function succeed() {
      cleanup();
      resolve();
    }

    /** @param {unknown} error */
    function fail(error) {
      cleanup();
      reject(error);
    }

    function checkProgress() {
      if (player.src != expectedSrc) {
        sourceChanged();
      } else if (player.currentTime > initialTime) {
        succeed();
      }
    }

    function checkEnded() {
      if (player.currentTime > initialTime) {
        succeed();
      } else {
        fail(new Error("Text-to-speech audio ended without playing"));
      }
    }

    function mediaError() {
      fail(new Error(player.error?.message || "Text-to-speech audio could not be decoded"));
    }

    function sourceChanged() {
      let error = new Error("Text-to-speech audio source changed");
      error.name = "AbortError";
      fail(error);
    }

    player.addEventListener("timeupdate", checkProgress);
    player.addEventListener("ended", checkEnded);
    player.addEventListener("error", mediaError);

    try {
      Promise.resolve(player.play()).catch(fail);
    } catch (error) {
      fail(error);
    }
  });
}
